/**
 * Validation utilities for form sections
 */

// Define mandatory fields for each section
export const mandatoryFields = {
  companyDetails: [
    'companyName',
    'jurisdiction',
    'registrationNumber',
    'incorporationDate',
    'registeredAddress',
    'registeredCity',
    'registeredPostcode',
    'registeredCountry',
    'businessActivity',
    'companyType'
  ],
  contactInformation: [
    'contactName',
    'position',
    'email',
    'phoneCountry',
    'phoneNumber',
    'address',
    'city',
    'postcode',
    'country'
  ],
  pepInformation: [
    'hasPep'
  ],
  sanctionsAudit: [
    'hasSanctions',
    'hasAudit'
  ],
  businessPresentation: [
    'businessDescription',
    'paymentFlows',
    'geographicDistribution',
    'customerTypes',
    'cryptoExperience',
    'riskAssessment'
  ],
  documentation: [
    'incorporationDocs',
    'memorandumArticles',
    'certificateIncorporation',
    'companyStructure',
    'signatoryList',
    'proofAddress'
  ]
};

// Conditional mandatory fields based on other field values
export const conditionalMandatoryFields = {
  companyDetails: {
    // If business address is different from registered address, business address fields are mandatory
    businessAddress: (data) => data.isDifferentBusinessAddress === true,
    businessCity: (data) => data.isDifferentBusinessAddress === true,
    businessPostcode: (data) => data.isDifferentBusinessAddress === true,
    businessCountry: (data) => data.isDifferentBusinessAddress === true
  },
  pepInformation: {
    // If hasPep is true, pepPersons array must have at least one person
    pepPersons: (data) => data.hasPep === true
  },
  sanctionsAudit: {
    // If hasSanctions is true, sanctionContext is required
    sanctionContext: (data) => data.hasSanctions === true,
    // If hasAudit is true, auditReport is required
    auditReport: (data) => data.hasAudit === true
  }
};

/**
 * Validate a single field
 */
export const validateField = (fieldName, value, sectionId, allData = {}) => {
  const errors = [];

  // Check if field is mandatory
  const isMandatory = mandatoryFields[sectionId]?.includes(fieldName);
  const isConditionallyMandatory = conditionalMandatoryFields[sectionId]?.[fieldName]?.(allData);

  // Special handling for file uploads and different value types
  const isEmpty = (val) => {
    if (val === null || val === undefined || val === '') return true;
    if (Array.isArray(val) && val.length === 0) return true;
    if (typeof val === 'object' && val !== null) {
      // For file uploads, check if file_id or original_file exists
      const fileFields = [
        'auditReport', 'incorporationDocs', 'memorandumArticles',
        'certificateIncorporation', 'companyStructure', 'signatoryList',
        'proofAddress', 'regulatoryLicenses', 'additionalDocs'
      ];
      if (fileFields.includes(fieldName)) {
        return !val.file_id && !val.original_file;
      }
    }
    return false;
  };

  if ((isMandatory || isConditionallyMandatory) && isEmpty(value)) {
    errors.push('This field is required');
  }

  // Field-specific validation
  switch (fieldName) {
    case 'email':
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        errors.push('Please enter a valid email address');
      }
      break;

    case 'phoneNumber':
      if (value && !/^\d+$/.test(value.replace(/\s/g, ''))) {
        errors.push('Phone number should contain only digits');
      }
      break;

    case 'website':
      if (value) {
        // More comprehensive URL validation
        try {
          const url = new URL(value);
          if (!['http:', 'https:'].includes(url.protocol)) {
            errors.push('Website URL must start with http:// or https://');
          }
        } catch (e) {
          errors.push('Please enter a valid website URL (e.g., https://example.com)');
        }
      }
      break;

    case 'incorporationDate':
      if (value) {
        const date = new Date(value);
        const now = new Date();
        if (date > now) {
          errors.push('Incorporation date cannot be in the future');
        }
      }
      break;

    case 'pepPersons':
      if (isConditionallyMandatory && (!value || value.length === 0)) {
        errors.push('At least one PEP person must be added');
      }
      break;

    case 'auditReport':
      // Special handling for file uploads - check if file exists
      if (isConditionallyMandatory && (!value || (!value.file_id && !value.original_file))) {
        errors.push('Audit report file is required when external audit status is Yes');
      }
      break;

    default:
      // No additional validation for other fields
      break;
  }

  return errors.length > 0 ? errors[0] : null;
};

/**
 * Validate an entire section
 */
export const validateSection = (sectionId, data) => {
  const errors = {};
  const fieldsToValidate = [
    ...(mandatoryFields[sectionId] || []),
    ...Object.keys(conditionalMandatoryFields[sectionId] || {})
  ];

  // Validate each field
  fieldsToValidate.forEach(fieldName => {
    const error = validateField(fieldName, data[fieldName], sectionId, data);
    if (error) {
      errors[fieldName] = error;
    }
  });

  // Special validation for PEP persons - only validate if hasPep is true
  if (sectionId === 'pepInformation' && data.hasPep === true) {
    // Check if pepPersons array exists and has at least one person
    if (!data.pepPersons || data.pepPersons.length === 0) {
      errors.pepPersons = 'At least one PEP person must be added when PEP status is Yes';
    } else {
      // Validate each PEP person
      data.pepPersons.forEach((person, index) => {
        const personErrors = {};
        const requiredPersonFields = ['fullName', 'position', 'passport', 'nationality', 'dateOfBirth', 'reason'];

        requiredPersonFields.forEach(field => {
          if (!person[field] || person[field] === '') {
            personErrors[field] = 'This field is required';
          }
        });

        if (Object.keys(personErrors).length > 0) {
          errors[`pepPerson_${index}`] = personErrors;
        }
      });
    }
  }

  return errors;
};

/**
 * Check if a section is complete (all mandatory fields filled)
 */
export const isSectionComplete = (sectionId, data) => {
  const errors = validateSection(sectionId, data);
  return Object.keys(errors).length === 0;
};

/**
 * Get missing mandatory fields for a section
 */
export const getMissingMandatoryFields = (sectionId, data) => {
  const errors = validateSection(sectionId, data);
  return Object.keys(errors);
};

/**
 * Format phone number to only contain digits
 */
export const formatPhoneNumber = (value) => {
  if (!value) return '';
  // Remove all non-digit characters
  return value.replace(/\D/g, '');
};

/**
 * Validate file upload for documentation section
 */
export const validateFileUpload = (fieldName, file) => {
  if (!file) return null;

  // Check file size (10MB limit)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    return 'File size must be less than 10MB';
  }

  // Check file type
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  if (!allowedTypes.includes(file.type)) {
    return 'Only PDF, DOC, and DOCX files are allowed';
  }

  return null;
};
